"use client";

import type { ReactNode } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { CheckCircle2, Flag, Info, Lock, TriangleAlert } from "lucide-react";

import { cn } from "@/lib/utils";
import { BAND_TEXT_ON_DARK, ratingBand } from "@/features/judge/lib/rating-band";
import { useRatedForMeRound } from "../hooks";
import { formatRating } from "../lib/division";
import type { PublicContestDetail } from "../types";
import { DivisionLine } from "./DivisionTag";

type Tone = "rated" | "plain" | "closed" | "notice";

const BOX: Readonly<Record<Tone, string>> = {
  rated: "border-emerald-400/30 bg-emerald-400/10",
  plain: "border-white/10 bg-white/5",
  closed: "border-white/15 bg-white/5",
  notice: "border-amber-400/30 bg-amber-400/10",
};

const ICON: Readonly<Record<Tone, string>> = {
  rated: "text-emerald-300",
  plain: "text-neutral-300",
  closed: "text-neutral-300",
  notice: "text-amber-300",
};

/**
 * The division's part of the register box (`EX-DIV03`, Wave 29 decisions 1 to
 * 4 and 8, the canvas's ContestPage and PhoneBangla boards), on the door's dark
 * card above the Register button.
 *
 * **Every answer is the server's `viewer.division`**: rated, unofficial or
 * closed, the rating it read (1200 for somebody new), whether the start has
 * fixed it, and what the entry records. This only words it. A signed-out
 * reader learns who the round rates before being asked to sign in; staff and
 * an operator account have their own notices and see the line only.
 *
 * Somebody this round does not rate is offered the next one that does, the
 * directory's own "Rated for me" read, when there is one.
 */
export function DivisionDoorNote({ contest, locale }: { contest: PublicContestDetail; locale: string }) {
  const t = useTranslations("contest.division");
  const division = contest.division ?? null;
  const viewer = contest.viewer;
  const mine = viewer.division ?? null;
  const offerOther =
    mine !== null && !viewer.isRegistered && (mine.standing === "unofficial" || mine.standing === "closed");
  const other = useRatedForMeRound(contest.slug, offerOther);

  if (!division) return null;

  // "Div. 3" never breaks between "Div." and its number inside a sentence.
  const label = (division.label ?? t("customTag")).replace(/ /g, "\u00a0");
  const line = (
    <DivisionLine division={division} locale={locale} tone="dark" className="text-neutral-300" />
  );
  const rating = (value: number) => (
    <b className={cn("font-bold", BAND_TEXT_ON_DARK[ratingBand(value)])}>{formatRating(value, locale)}</b>
  );

  // Signed out, or somebody whose own notice says why they do not compete.
  if (!viewer.isAuthenticated || !mine || viewer.staffRole || viewer.operatorAccount) {
    return (
      <Box tone="plain" icon={<Info className="h-4 w-4" aria-hidden />} testId="division-note-line">
        <p>{line}</p>
        {!viewer.isAuthenticated ? <p className="text-neutral-400">{t("signedOut")}</p> : null}
      </Box>
    );
  }

  const otherLink = other ? (
    <p className="text-neutral-300" data-testid="division-other-round">
      {t.rich("otherRound", {
        link: (chunks) => (
          <Link
            href={`/${locale}/contests/${other.slug}`}
            className="font-semibold text-accent-peach underline underline-offset-2 transition-colors duration-200 hover:text-white"
          >
            {chunks}
          </Link>
        ),
        title: [(locale === "bn" ? other.title_bn : null) ?? other.title_en, other.division?.label]
          .filter(Boolean)
          .join(", "),
      })}
    </p>
  ) : null;

  if (viewer.isRegistered) {
    // Decision 8: a rating that crossed the band after registering. Before the
    // start the entry is asked again; the box says which way it will go.
    if (!mine.fixed && mine.registeredAs !== null && mine.registeredAs !== mine.standing) {
      return (
        <Box tone="notice" icon={<TriangleAlert className="h-4 w-4" aria-hidden />} testId="division-note-crossed">
          <p className="font-semibold text-white">{t("crossedTitle")}</p>
          <p>
            {t.rich(mine.standing === "unofficial" ? "crossedOut" : "crossedIn", {
              label,
              rating: () => rating(mine.rating),
            })}
          </p>
          <p>{line}</p>
        </Box>
      );
    }
    if (mine.standing === "unofficial") {
      return (
        <Box tone="plain" icon={<Info className="h-4 w-4" aria-hidden />} testId="division-note-registered-unofficial">
          <p className="font-semibold text-white">{t("registeredUnofficialTitle")}</p>
          <p>{t("registeredUnofficialBody")}</p>
          <p>{line}</p>
        </Box>
      );
    }
    return (
      <Box tone="rated" icon={<CheckCircle2 className="h-4 w-4" aria-hidden />} testId="division-note-registered-rated">
        <p className="font-semibold text-white">{t(mine.fixed ? "ratedFixedTitle" : "ratedTitle")}</p>
        <p>{line}</p>
      </Box>
    );
  }

  if (mine.standing === "closed") {
    return (
      <Box tone="closed" icon={<Lock className="h-4 w-4" aria-hidden />} testId="division-note-closed">
        <p className="font-semibold text-white">{t("closedTitle")}</p>
        <p>
          {t.rich(mine.newcomer ? "closedBodyNew" : "closedBody", { label, rating: () => rating(mine.rating) })}
        </p>
        <p>{line}</p>
        {otherLink}
      </Box>
    );
  }

  if (mine.standing === "unofficial") {
    return (
      <Box tone="plain" icon={<Info className="h-4 w-4" aria-hidden />} testId="division-note-unofficial">
        <p className="font-semibold text-white">{t("unofficialTitle")}</p>
        <p>{t.rich("unofficialBody", { label, rating: () => rating(mine.rating) })}</p>
        <p>{line}</p>
        {otherLink}
      </Box>
    );
  }

  return (
    <Box
      tone="rated"
      icon={mine.newcomer ? <Flag className="h-4 w-4" aria-hidden /> : <CheckCircle2 className="h-4 w-4" aria-hidden />}
      testId={mine.newcomer ? "division-note-new" : "division-note-rated"}
    >
      <p className="font-semibold text-white">{t(mine.newcomer ? "newTitle" : "ratedTitle")}</p>
      <p>
        {mine.newcomer
          ? t.rich("newBody", { label, rating: () => rating(mine.rating) })
          : t.rich("ratedBody", { label, rating: () => rating(mine.rating) })}
      </p>
      <p>{line}</p>
      {mine.fixed ? null : <p className="text-xs text-neutral-400">{t("startDecides")}</p>}
    </Box>
  );
}

function Box({
  tone,
  icon,
  testId,
  children,
}: {
  tone: Tone;
  icon: ReactNode;
  testId: string;
  children: ReactNode;
}) {
  return (
    <div
      data-testid={testId}
      className={cn("mb-4 flex items-start gap-2.5 rounded-[10px] border px-4 py-3 text-sm text-neutral-200", BOX[tone])}
    >
      <span className={cn("mt-0.5 shrink-0", ICON[tone])}>{icon}</span>
      <div className="min-w-0 space-y-1.5 leading-relaxed">{children}</div>
    </div>
  );
}
